import { useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { CONSTRAINTS, type OutputLanguage } from "@meeting-notes/shared";
import { useApi } from "../lib/api";
import { uploadWithProgress } from "../lib/upload";

export function UploadPage() {
  const api = useApi();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [lang, setLang] = useState<OutputLanguage>("ko");
  const [hint, setHint] = useState("auto");
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isMp3 = (f: File) => f.type === "audio/mpeg" || /\.mp3$/i.test(f.name);

  async function submit() {
    if (!file) return;
    setError(null);
    if (!isMp3(file)) return setError("mp3 파일만 업로드할 수 있습니다.");
    if (file.size > CONSTRAINTS.maxUploadBytes) return setError("파일이 너무 큽니다 (최대 500MB).");
    try {
      setProgress(0);
      const res = await api.createMeeting({ title: title.trim() || undefined, fileName: file.name, fileSize: file.size, contentType: "audio/mpeg", outputLanguage: lang, languageHint: hint === "auto" ? undefined : hint });
      await uploadWithProgress(res.upload.url, file, res.upload.headers, setProgress);
      await qc.invalidateQueries({ queryKey: ["meetings"] });
      nav(`/meetings/${res.meeting.meetingId}`);
    } catch (e) {
      setProgress(null);
      setError((e as Error).message);
    }
  }

  return (
    <div className="px-4 pt-4">
      <h1 className="text-2xl font-bold mb-4">회의 업로드</h1>
      <label className="block mb-4">
        <span className="text-sm text-slate-300">mp3 파일</span>
        <input type="file" accept="audio/mpeg,.mp3" className="mt-1 block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-slate-800 file:px-3 file:py-2 file:text-slate-100" onChange={(e) => setFile(e.target.files?.[0] ?? null)} disabled={progress !== null} />
        {file && <p className="mt-1 text-xs text-slate-400">{file.name} · {(file.size / 1024 / 1024).toFixed(1)}MB</p>}
      </label>
      <label className="block mb-4">
        <span className="text-sm text-slate-300">제목 (선택)</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="예: 9월 첫째 주 주간회의" className="mt-1 w-full rounded-lg bg-slate-900 border border-slate-800 px-3 py-2.5 text-base" disabled={progress !== null} />
      </label>
      <div className="grid grid-cols-2 gap-3 mb-6">
        <label className="block">
          <span className="text-sm text-slate-300">결과 언어</span>
          <select value={lang} onChange={(e) => setLang(e.target.value as OutputLanguage)} className="mt-1 w-full rounded-lg bg-slate-900 border border-slate-800 px-3 py-2.5 text-base" disabled={progress !== null}>
            <option value="ko">한국어</option>
            <option value="en">English</option>
            <option value="auto">회의 언어 그대로</option>
          </select>
        </label>
        <label className="block">
          <span className="text-sm text-slate-300">회의 언어 힌트</span>
          <select value={hint} onChange={(e) => setHint(e.target.value)} className="mt-1 w-full rounded-lg bg-slate-900 border border-slate-800 px-3 py-2.5 text-base" disabled={progress !== null}>
            <option value="auto">자동 감지</option>
            <option value="ko">한국어</option>
            <option value="en">English</option>
            <option value="ja">日本語</option>
            <option value="zh">中文</option>
          </select>
        </label>
      </div>
      {progress !== null && (
        <div className="mb-4">
          <div className="h-2 rounded bg-slate-800 overflow-hidden"><div className="h-full bg-indigo-500 transition-all" style={{ width: `${progress}%` }} /></div>
          <p className="mt-1 text-xs text-slate-400">{progress < 100 ? `업로드 중 ${progress}%` : "업로드 완료, 처리 시작 중…"}</p>
        </div>
      )}
      {error && <p className="mb-4 text-sm text-red-300">{error}</p>}
      <button className="tap w-full rounded-xl bg-indigo-500 py-3 font-semibold disabled:opacity-50" disabled={!file || progress !== null} onClick={() => void submit()}>
        업로드하고 분석 시작
      </button>
      <p className="mt-3 text-xs text-slate-500">업로드가 끝나면 앱을 닫아도 됩니다. 처리가 완료되면 알림을 보내 드립니다(설정에서 알림 켜기).</p>
    </div>
  );
}
